import React, { createContext, useCallback, useContext, useState, useRef } from "react";
import { axiosInstance } from "../utils/axios";
const StandingsContext = createContext({});

export const useStandingsContext = () => {
  return useContext(StandingsContext);
};

export const StandingsProvider = ({ children }) => {
  const [standings, setStandings] = useState({});
  // "loading" | "ready" | "error" per season id. Without this a failed fetch is
  // indistinguishable from one still in flight, and consumers skeleton forever.
  const [standingsStatus, setStandingsStatus] = useState({});
  const loadingSeasonIds = useRef(new Set());
  const loadedSeasonIds = useRef(new Set());

  const fetchStandingsDrivers = useCallback(async (season) => {
    const fetchDetailed = true;

    const { data } = await axiosInstance.get(`/seasons/${season}/standings`, {
      params: { group: "drivers", detailed: fetchDetailed },
    });
    const result = {};
    const mapItem = data?._embedded?.standings || data?.standings || data || {};
    Object.entries(mapItem).forEach(([division, list]) => {
      const drivers = Array.isArray(list) ? list : (list._embedded?.standings || []);
      result[division] = drivers.filter((d) => d.points > 0);
    });
    return result;
  }, []);

  const fetchStandingsTeams = useCallback(async (season) => {
    const { data } = await axiosInstance.get(`/seasons/${season}/standings`, {
      params: { group: "teams", detailed: true },
    });
    const result = {};
    const mapItem = data?._embedded?.standings || data?.standings || data || {};
    Object.entries(mapItem).forEach(([division, list]) => {
      const teams = Array.isArray(list) ? list : (list._embedded?.standings || []);
      result[division] = teams;
    });
    return result;
  }, []);

  const loadStandings = useCallback(
    async (season) => {
      // Independent requests: no reason for the second to wait on the first
      const [drivers, teams] = await Promise.all([
        fetchStandingsDrivers(season),
        fetchStandingsTeams(season),
      ]);
      return { drivers, teams };
    },
    [fetchStandingsDrivers, fetchStandingsTeams],
  );

  const loadSeason = useCallback(
    async (season) => {
      if (
        season < 1 ||
        loadedSeasonIds.current.has(season) ||
        loadingSeasonIds.current.has(season)
      ) {
        return;
      }
      loadingSeasonIds.current.add(season);
      setStandingsStatus((prevStatus) => ({ ...prevStatus, [season]: "loading" }));
      try {
        const seasonStandings = await loadStandings(season);

        const distributed = {};
        distributed[season] = { drivers: {}, teams: {} };

        const processGroup = (groupName) => {
          const group = seasonStandings[groupName];
          if (!group) return;

          Object.entries(group).forEach(([key, list]) => {
            const match = key.match(/^(\d+)_(.+)$/);
            if (match) {
              const childId = Number(match[1]);
              const realDiv = match[2];

              if (!distributed[childId]) {
                distributed[childId] = { drivers: {}, teams: {} };
              }
              distributed[childId][groupName][realDiv] = list;
            } else {
              distributed[season][groupName][key] = list;
            }
          });
        };

        processGroup("drivers");
        processGroup("teams");

        // A parent season distributes into its children, so every id it produced counts as loaded
        Object.keys(distributed).forEach((sId) => loadedSeasonIds.current.add(Number(sId)));

        setStandings((prevData) => {
          const updated = { ...prevData };
          Object.entries(distributed).forEach(([sId, data]) => {
            updated[sId] = data;
          });
          return updated;
        });
        setStandingsStatus((prevStatus) => {
          const updated = { ...prevStatus };
          Object.keys(distributed).forEach((sId) => {
            updated[sId] = "ready";
          });
          updated[season] = "ready";
          return updated;
        });
      } catch (e) {
        console.error("Failed to load standings for season", season, e);
        setStandingsStatus((prevStatus) => ({ ...prevStatus, [season]: "error" }));
      } finally {
        loadingSeasonIds.current.delete(season);
      }
    },
    [loadStandings],
  );

  return (
    <StandingsContext.Provider
      value={{
        standings,
        standingsStatus,
        loadSeason,
      }}
    >
      {children}
    </StandingsContext.Provider>
  );
};
